import {apiService, getAuthValues, setAuthHeader} from "./api";
import {Chat, Message} from "../models";
import {Maybe} from "./requests";

export type Read = {messageID: string, timestamp: number};
type MessageResponse = {
    senderID: string
    senderName: string
    chatID: string
    readers: string[]
    messageID: string
    content: string
    timestamp: number
};
type ChatResponse = {
    chatID: string
    createdAt: string
    messages?: MessageResponse[]
    status: Chat["status"]
}

function toMessage(val: MessageResponse): Message {
    return {
        status: "sent",
        chatID: val.chatID,
        id: val.messageID,
        content: val.content,
        readers: val.readers,
        senderId: val.senderID,
        senderName: val.senderName,
        timestamp: val.timestamp,
    };
}

function toChat(val: ChatResponse): Chat {
    return {
        createdAt: val.createdAt,
        id: val.chatID,
        messages: val.messages?.map(toMessage) ?? [],
        status: val.status
    };
}

export async function fetchChats(status?: "active" | "archived"): Promise<Chat[]>{
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    res = await apiService.get<{chats: ChatResponse[]}>("/request/chats");
    return res.data.chats.map(toChat);
}

export async function sendMessage(chatId: string, content: string): Promise<Maybe<Message>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(`${token}`);
    try {
        res = await apiService.post<{message: MessageResponse}>(
            `/request/new-message`,
            {chatId, content}
        );
        return {type: "success", value: toMessage(res.data.message)};
    } catch (e: any) {
        res = e.response?.data?.message ?? "Failed to send message";
        return {type: "error", message: res};
    }
}

export async function readMessages(id: string, messages: Read[]): Promise<Maybe<{message: string}>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(token!);
    try {
        res = await apiService.post<{message: string}>(
            `/request/read-messages`,
            {chatId: id, messages}
        );
        return {type: "success", value: res.data};
    } catch (e: any) {
        res = e.response?.data?.message ?? "Failed to acknowledge messages read";
        return {type: "error", message: res};
    }
}

export async function getMessages(chatId: string): Promise<Maybe<Message[]>> {
    const {token} = getAuthValues();
    let res;
    setAuthHeader(`${token}`);
    try {
        res = await apiService.get<{messages: MessageResponse[]}>(
            `/request/messages?chatId=${chatId}`
        );
        return {type: "success", value: res.data.messages.map(toMessage)};
    } catch (e: any) {
        res = e.response?.data?.message ?? "Failed to retrieve messages";
        return {type: "error", message: res};
    }
}
